// @ts-nocheck
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCurrency, getConfig } from '../../../utils/currencyHelper';
import { planIsSoldOut, planIsPastDeadline } from '../../../utils/booking';
import { WarningCircle, XCircle } from '@phosphor-icons/react';
import Editor from '../../Editor/Editor';
import PropTypes from 'prop-types';
import { isPercentageBasedAddOn } from '../../../utils/percentageAddOns';
import './PaymentAddOnCard.css';

/**
 * PaymentAddOnCard component for displaying and selecting add-ons in secondary payment
 *
 * @namespace Components
 *
 * @param {Object} props - Component props
 * @param {Object} props.addon - The add-on data
 * @param {Array} props.passengers - List of passengers from booking
 * @param {Array} props.selectedPassengerIds - IDs of passengers already selected for this add-on
 * @param {Array} props.existingPassengerIds - IDs of passengers who already have this add-on (locked)
 * @param {Function} props.onPassengerToggle - Callback when passenger selection changes
 * @param {boolean} props.isStaff - Whether current user is staff
 * @param {...any} props.rest - Additional props to be spread on the parent element
 *
 * @returns {JSX.Element} React element representing the payment add-on card
 */
const PaymentAddOnCard = ({
  addon,
  passengers,
  selectedPassengerIds = [],
  existingPassengerIds = [],
  onPassengerToggle,
  getPassengerAddOnAmount,
  enablePercentageBasedAddons = false,
  // isStaff reserved for future use (e.g., staff-only actions)
  // eslint-disable-next-line no-unused-vars
  isStaff = false,
  ...rest
}) => {
  const { t } = useTranslation(['trip', 'common', 'bookings']);
  const { currencyCode } = getConfig();
  const isPercentagePricing = isPercentageBasedAddOn(
    addon,
    enablePercentageBasedAddons
  );

  // Check if this add-on is sold out or past deadline
  const isSoldOut = planIsSoldOut(addon);
  const isPastDeadline = planIsPastDeadline(addon);
  // Disable the card if sold out OR past deadline (regardless of auto-hide settings)
  const isDisabled = isSoldOut || isPastDeadline;

  // Calculate remaining capacity for this add-on
  const hasLimitedAvailability = addon?.maxavailable && addon.maxavailable > 0;
  const numSelected = selectedPassengerIds.length;
  // Backend returns 'available' field (already calculated as maxavailable - sold)
  const backendAvailable = addon?.available ?? null;
  const remainingCapacity =
    hasLimitedAvailability && backendAvailable !== null
      ? backendAvailable - numSelected
      : Infinity;

  const handlePassengerToggle = (passengerId) => {
    if (isDisabled) return;

    // Don't allow toggling existing selections
    if (existingPassengerIds.includes(passengerId)) return;

    const isCurrentlySelected = selectedPassengerIds.includes(passengerId);

    // If trying to select and no capacity remaining, don't allow
    if (!isCurrentlySelected && remainingCapacity <= 0) return;

    onPassengerToggle(addon?.tripaddonid, passengerId);
  };

  const isPassengerSelected = (passengerId) => {
    return (
      selectedPassengerIds.includes(passengerId) ||
      existingPassengerIds.includes(passengerId)
    );
  };

  const isPassengerLocked = (passengerId) => {
    return existingPassengerIds.includes(passengerId);
  };

  // Format price with sign
  const formatPrice = (price) => {
    const formattedPrice = formatCurrency(price, { currencyCode });
    return `${formattedPrice}`;
  };

  return (
    <Box
      className={`payment-addon-card ${isDisabled ? 'payment-addon-card--disabled' : ''}`}
      data-testid='payment-addon-card'
      {...rest}
    >
      <Box className='payment-addon-card__content'>
        {/* Header Row: Title → Price */}
        <Box className='payment-addon-card__header-row'>
          <Box className='payment-addon-card__title-section'>
            <h3 className='payment-addon-card__title'>{addon?.title}</h3>
          </Box>
          {!isPercentagePricing && (
            <Box className='payment-addon-card__price'>
              <span className='payment-addon-card__price-text'>
                {formatPrice(addon?.priceperperson || 0)}
              </span>
            </Box>
          )}
        </Box>

        {/* Description */}
        {addon?.description && addon.description !== '' && (
          <Box className='payment-addon-card__description'>
            <Editor
              content={addon.description}
              editable={false}
              isPreviewing={true}
              id={`payment-addon-card-description-editor-${addon?.tripaddonid}`}
              className='third-output-editor--full-width'
            />
          </Box>
        )}

        {/* Status Badges */}
        {(isPastDeadline || isSoldOut) && (
          <Box className='payment-addon-card__badges'>
            {isPastDeadline && (
              <span className='addon-pastdeadline-badge'>
                <WarningCircle size={16} />
                {t('trip:bookingDeadlinePassed', 'Booking Deadline Passed')}
              </span>
            )}
            {isSoldOut && (
              <span className='addon-soldout-badge'>
                <XCircle size={16} />
                {t('trip:soldOut', 'Sold Out')}
              </span>
            )}
          </Box>
        )}

        {/* Passenger Selection */}
        {!isDisabled && passengers && passengers.length > 0 && (
          <Box className='payment-addon-card__passengers-section'>
            <hr className='payment-addon-card__separator' />
            <label className='payment-addon-card__passengers-label'>
              {t('trip:selectPassengers', 'Select passengers:')}
            </label>
            <Box
              className={`payment-addon-card__passengers-grid ${
                isPercentagePricing
                  ? 'payment-addon-card__passengers-grid--percentage'
                  : ''
              }`}
            >
              {passengers.map((passenger, index) => {
                const passengerId =
                  passenger.bookingpassengerid ||
                  passenger.id ||
                  `passenger-${index}`;
                const passengerName =
                  passenger.name ||
                  `${t('trip:passenger', 'Passenger')} ${index + 1}`;

                const isSelected = isPassengerSelected(passengerId);
                const isLocked = isPassengerLocked(passengerId);
                // Disable checkbox if: locked (already purchased) OR (not selected AND no remaining capacity)
                const isCheckboxDisabled =
                  isLocked || (!isSelected && remainingCapacity <= 0);
                const calculatedAmount = getPassengerAddOnAmount(
                  addon,
                  passenger
                );
                const label = isPercentagePricing ? (
                  <Box className='payment-addon-card__passenger-row-label'>
                    <span className='payment-addon-card__passenger-name'>
                      {passengerName}
                    </span>
                    <span className='payment-addon-card__passenger-plan'>
                      {passenger?.passengerPlanTitle}
                    </span>
                    <span className='payment-addon-card__passenger-price'>
                      {formatCurrency(calculatedAmount)}
                    </span>
                  </Box>
                ) : (
                  passengerName
                );

                return (
                  <FormControlLabel
                    key={passengerId}
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handlePassengerToggle(passengerId)}
                        disabled={isCheckboxDisabled}
                        sx={{
                          color: 'var(--gray-400, #98a2b3)',
                          '&.Mui-checked': {
                            color: isLocked
                              ? 'var(--success-600, #079455)'
                              : 'var(--primary-600, #0d69d4)'
                          },
                          '&.Mui-disabled': {
                            color: 'var(--gray-300, #d0d5dd)'
                          }
                        }}
                      />
                    }
                    label={label}
                    className={`payment-addon-card__passenger-checkbox ${
                      isPercentagePricing
                        ? 'payment-addon-card__passenger-checkbox--percentage'
                        : ''
                    }`}
                  />
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

PaymentAddOnCard.propTypes = {
  addon: PropTypes.object.isRequired,
  passengers: PropTypes.array,
  selectedPassengerIds: PropTypes.array,
  existingPassengerIds: PropTypes.array,
  onPassengerToggle: PropTypes.func.isRequired,
  getPassengerAddOnAmount: PropTypes.func.isRequired,
  enablePercentageBasedAddons: PropTypes.bool,
  isStaff: PropTypes.bool
};

export default PaymentAddOnCard;
